/**
 * NCC: nested facet values ("Nested filter values" on the Filters block, rendered by snippets/list-filter.liquid
 * and snippets/ncc-facet-tree-children.liquid).
 *
 * Every value of the Category facet is one OR'd Search & Discovery tag filter value, so a child (Sheep) and its
 * parent (Cheeses) must never both be in the URL - the parent would win and return every cheese. Horizon's
 * facet-inputs-component serialises the form from a capture-phase document listener (component.js), so this
 * one listens on the window, the first stop of the capture phase, and settles the tree before it: ticking a
 * child unticks its parent, ticking the parent unticks its children, and unticking the last child ticks the
 * parent again, so the shopper steps back up to "all cheeses" instead of losing the sub-list.
 */
const PARENT = '.ncc-facet-tree__parent';
const CHILD = '.ncc-facet-tree__child';
const CHILDREN = '.ncc-facet-tree__children';
const PARTIAL = 'ncc-facet-tree__children--partial';

/**
 * @param {Element} parentItem
 */
function inputsOf(parentItem) {
  const parentInput = parentItem.querySelector(':scope > .checkbox > .checkbox__input');
  const childInputs = [...parentItem.querySelectorAll(`${CHILD} .checkbox__input`)].filter(
    (input) => input instanceof HTMLInputElement
  );
  return { parentInput: parentInput instanceof HTMLInputElement ? parentInput : null, childInputs };
}

/**
 * Mirrors the tree state: the parent is indeterminate (and the list carries the partial class that draws the
 * dash) while a child is ticked, and the children list is open while the parent or a child is ticked.
 * @param {Element} parentItem
 */
function syncParent(parentItem) {
  const { parentInput, childInputs } = inputsOf(parentItem);
  if (!parentInput) return;

  const partial = !parentInput.checked && childInputs.some((input) => input.checked);
  parentInput.indeterminate = partial;

  const list = parentItem.querySelector(`:scope > ${CHILDREN}`);
  if (list instanceof HTMLElement) {
    list.classList.toggle(PARTIAL, partial);
    const hide = !(parentInput.checked || partial);
    if (list.hidden !== hide) list.hidden = hide;
  }
}

window.addEventListener(
  'change',
  (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox') return;

    const parentItem = input.closest(PARENT);
    if (!parentItem) return;

    const { parentInput, childInputs } = inputsOf(parentItem);
    if (!parentInput) return;

    if (input === parentInput) {
      if (input.checked) {
        for (const child of childInputs) child.checked = false;
      }
    } else if (input.closest(CHILD)) {
      if (input.checked) {
        parentInput.checked = false;
      } else if (!childInputs.some((child) => child.checked)) {
        parentInput.checked = true;
      }
    }

    syncParent(parentItem);
  },
  true
);

// The section re-renders after every change (and the drawer holds a second copy of the form): keep the
// indeterminate flag, which is a property and not an attribute, in step with what Liquid rendered.
let queued = false;
const syncAll = () => {
  queued = false;
  document.querySelectorAll(PARENT).forEach(syncParent);
};
new MutationObserver(() => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(syncAll);
}).observe(document.documentElement, { childList: true, subtree: true });
syncAll();
